import { useEffect, useCallback, useRef } from 'react';
import { reportWebVital, shouldReportMetric } from '@/lib/webVitalsAnalytics';

// Core Web Vitals thresholds
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },    // Largest Contentful Paint
  FID: { good: 100, poor: 300 },       // First Input Delay
  CLS: { good: 0.1, poor: 0.25 },      // Cumulative Layout Shift
  FCP: { good: 1800, poor: 3000 },     // First Contentful Paint
  TTFB: { good: 800, poor: 1800 },     // Time to First Byte
  INP: { good: 200, poor: 500 },       // Interaction to Next Paint
};

type MetricName = keyof typeof THRESHOLDS;

interface WebVitalMetric {
  name: MetricName;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

// Get rating based on value
const getRating = (name: MetricName, value: number): 'good' | 'needs-improvement' | 'poor' => {
  const threshold = THRESHOLDS[name];
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
};

// Generate unique ID
const generateId = () => `v${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Hook to track Web Vitals
export const useWebVitals = (onReport?: (metric: WebVitalMetric) => void) => {
  const metricsRef = useRef<Map<string, WebVitalMetric>>(new Map());

  const reportMetricInternal = useCallback((metric: WebVitalMetric) => {
    metricsRef.current.set(metric.name, metric);
    
    // Log in development
    if (process.env.NODE_ENV === 'development') {
      const color = metric.rating === 'good' ? '#0cce6b' : 
                    metric.rating === 'needs-improvement' ? '#ffa400' : '#ff4e42';
      console.log(
        `%c[Web Vitals] ${metric.name}: ${metric.value.toFixed(2)}ms (${metric.rating})`,
        `color: ${color}; font-weight: bold;`
      );
    }
    
    // Send to analytics (respects sampling)
    if (shouldReportMetric()) {
      reportWebVital(metric);
    }
    
    onReport?.(metric);
  }, [onReport]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Largest Contentful Paint (LCP)
    const lcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
      
      if (lastEntry) {
        reportMetricInternal({
          name: 'LCP',
          value: lastEntry.startTime,
          rating: getRating('LCP', lastEntry.startTime),
          delta: lastEntry.startTime,
          id: generateId(),
        });
      }
    });

    // First Input Delay (FID) / Interaction to Next Paint (INP)
    const fidObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach((entry: PerformanceEntry & { processingStart: number; startTime: number }) => {
        const delay = entry.processingStart - entry.startTime;
        
        reportMetricInternal({
          name: 'FID',
          value: delay,
          rating: getRating('FID', delay),
          delta: delay,
          id: generateId(),
        });
      });
    });

    // Cumulative Layout Shift (CLS)
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach((entry: PerformanceEntry & { hadRecentInput: boolean; value: number }) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
          
          reportMetricInternal({
            name: 'CLS',
            value: clsValue,
            rating: getRating('CLS', clsValue),
            delta: entry.value,
            id: generateId(),
          });
        }
      });
    });

    // First Contentful Paint (FCP)
    const fcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const fcpEntry = entries.find(e => e.name === 'first-contentful-paint');
      
      if (fcpEntry) {
        reportMetricInternal({
          name: 'FCP',
          value: fcpEntry.startTime,
          rating: getRating('FCP', fcpEntry.startTime),
          delta: fcpEntry.startTime,
          id: generateId(),
        });
      }
    });

    try {
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      fidObserver.observe({ type: 'first-input', buffered: true });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
      fcpObserver.observe({ type: 'paint', buffered: true });
    } catch (e) {
      // PerformanceObserver not supported
      console.warn('PerformanceObserver not fully supported');
    }

    // Time to First Byte (TTFB)
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigationEntry) {
      const ttfb = navigationEntry.responseStart - navigationEntry.requestStart;
      reportMetricInternal({
        name: 'TTFB',
        value: ttfb,
        rating: getRating('TTFB', ttfb),
        delta: ttfb,
        id: generateId(),
      });
    }

    return () => {
      lcpObserver.disconnect();
      fidObserver.disconnect();
      clsObserver.disconnect();
      fcpObserver.disconnect();
    };
  }, [reportMetricInternal]);

  return {
    getMetrics: () => Object.fromEntries(metricsRef.current),
    getMetric: (name: MetricName) => metricsRef.current.get(name),
  };
};

// Utility to optimize LCP - more aggressive
export const optimizeLCP = () => {
  // Find and prioritize LCP candidates
  const lcpCandidates = document.querySelectorAll('img[data-lcp], .hero-image, [class*="hero"], main > section:first-child img');
  
  lcpCandidates.forEach((el) => {
    if (el instanceof HTMLImageElement) {
      // Set high priority for LCP images
      el.fetchPriority = 'high';
      el.loading = 'eager';
      el.decoding = 'sync';
      
      // Preload if not already loaded
      if (!el.complete && el.src) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = el.src;
        link.fetchPriority = 'high';
        document.head.appendChild(link);
      }
    }
  });

  // Optimize largest visible text block for FCP
  const mainContent = document.querySelector('main, [role="main"], #root > div');
  if (mainContent) {
    (mainContent as HTMLElement).style.contentVisibility = 'auto';
  }
};

// Utility to reduce CLS - with explicit dimensions
export const reserveSpaceForImages = () => {
  document.querySelectorAll('img:not([width]):not([height]):not([data-lcp])').forEach(img => {
    if (img instanceof HTMLImageElement) {
      // Only lazy load below-the-fold images
      const rect = img.getBoundingClientRect();
      const isAboveFold = rect.top < window.innerHeight;
      
      img.style.aspectRatio = '16/9';
      img.loading = isAboveFold ? 'eager' : 'lazy';
      img.decoding = isAboveFold ? 'sync' : 'async';
      
      // Prevent CLS with explicit containment
      img.style.contain = 'layout';
    }
  });
};

// Utility to improve FID/INP - break up long tasks
export const deferNonCriticalJS = (callback: () => void) => {
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(callback, { timeout: 1000 });
  } else {
    setTimeout(callback, 50);
  }
};

// Optimize render-blocking resources
export const optimizeRenderBlocking = () => {
  // Move non-critical stylesheets to print media
  document.querySelectorAll('link[rel="stylesheet"]:not([data-critical])').forEach(link => {
    const stylesheet = link as HTMLLinkElement;
    if (!stylesheet.href.includes('critical')) {
      stylesheet.media = 'print';
      stylesheet.onload = () => { stylesheet.media = 'all'; };
    }
  });
};

// Preload critical fonts
export const preloadFonts = () => {
  const fontUrls = [
    'https://fonts.gstatic.com/s/inter/v13/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiJ-Ek-_EeA.woff2'
  ];
  
  fontUrls.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = 'font/woff2';
    link.href = url;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
};

// Initialize optimizations - run immediately
export const initWebVitalsOptimizations = () => {
  // Critical path - run synchronously
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      optimizeLCP();
      reserveSpaceForImages();
    });
  } else {
    // Already loaded
    optimizeLCP();
    reserveSpaceForImages();
  }
  
  // Non-critical - defer
  deferNonCriticalJS(() => {
    optimizeRenderBlocking();
  });
};
